import { useMemo, useState } from "react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axiosClient from "../axiosClient";

function PaymentForm({
  programOption,
  amountDisplay,
  baseDisplay,
  feeDisplay,
  studentName,
  onMissingStudentName,
  onSuccess,
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || isSubmitting) {
      return;
    }

    const trimmedName = (studentName || "").trim();
    if (!trimmedName) {
      setErrorMessage("Please enter the student name above before paying.");
      if (onMissingStudentName) {
        onMissingStudentName();
      }
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      // Validate card details before creating anything on Stripe.
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || "Please check your payment details.");
        return;
      }

      const intentResponse = await axiosClient.post("/middleSchoolPrepRegistrations", {
        programOption,
        studentName: trimmedName,
      });
      const { clientSecret, paymentIntentId } = intentResponse.data;

      if (!clientSecret) {
        setErrorMessage("Unable to start payment. Please try again.");
        return;
      }

      const returnUrl = `${window.location.origin}${window.location.pathname}?paid=1`;

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: "if_required",
      });

      if (error) {
        setErrorMessage(error.message || "Payment failed. Please try again.");
        return;
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        try {
          await axiosClient.post("/middleSchoolPrepRegistrations/confirm", {
            paymentIntentId: paymentIntent.id || paymentIntentId,
            programOption,
            studentName: trimmedName,
          });
        } catch (confirmError) {
          setErrorMessage(
            confirmError?.response?.data?.message ||
              "Payment succeeded but confirmation failed. Please contact the school."
          );
          return;
        }

        onSuccess();
        return;
      }

      setErrorMessage("Payment is still processing. Please wait a moment.");
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "We could not complete payment. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="middle-school-prep-stripe-form" onSubmit={handleSubmit}>
      <div className="middle-school-prep-stripe-summary">
        <p>
          Tuition: <strong>{baseDisplay}</strong>
        </p>
        <p>
          Stripe processing fees: <strong>{feeDisplay}</strong>
        </p>
        <p className="middle-school-prep-stripe-total">
          Total: <strong>{amountDisplay}</strong>
        </p>
      </div>

      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />

      {errorMessage && (
        <span className="registration-form-error">{errorMessage}</span>
      )}

      <div className="registration-form-actions">
        <input
          type="submit"
          value={
            isSubmitting
              ? "Processing..."
              : `Pay ${amountDisplay} with Credit Card`
          }
          disabled={!stripe || !elements || isSubmitting}
        />
      </div>
    </form>
  );
}

const MiddleSchoolPrepStripeCheckout = ({
  publishableKey,
  amountCents,
  programOption,
  amountDisplay,
  baseDisplay,
  feeDisplay,
  studentName,
  onMissingStudentName,
  onSuccess,
}) => {
  const stripePromise = useMemo(
    () => loadStripe(publishableKey),
    [publishableKey]
  );

  if (!publishableKey || !amountCents) {
    return null;
  }

  return (
    <div className="middle-school-prep-stripe-embed">
      <Elements
        stripe={stripePromise}
        options={{
          mode: "payment",
          amount: amountCents,
          currency: "usd",
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#1565c0",
              borderRadius: "8px",
            },
          },
        }}
      >
        <PaymentForm
          programOption={programOption}
          amountDisplay={amountDisplay}
          baseDisplay={baseDisplay}
          feeDisplay={feeDisplay}
          studentName={studentName}
          onMissingStudentName={onMissingStudentName}
          onSuccess={onSuccess}
        />
      </Elements>
    </div>
  );
};

export default MiddleSchoolPrepStripeCheckout;
